import { waitForItPromise } from 'src/lib/utils';
import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import {
  CollideEvent,
  CollideId,
  isOneHitCollider,
  isPowerCollider,
} from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BarrelBlockEntity } from './BarrelBlockEntity';
import { blockNearestNeighbors } from '../../util/blockTools';
import { BarrelBlockEffect } from './BarrelBlockEffect';
import { app } from '../../app';

export class BarrelBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: BarrelBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: BarrelBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    this.attack();
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    // If the collider is a one hit collider, destroy in a single attack
    if (isOneHitCollider(event.collider?.type)) {
      this.entity.c.blockBarrel.count = 1;
    }

    this.attack();
    return true;
  }

  private async attack() {
    const entity = this.entity;
    const component = entity.c.blockBarrel;

    if (component.count <= 1) {
      await this.explodeBarrel(entity);
    } else {
      app.sound.play('puzzle-cage-destroy');
      new BarrelBlockEffect(this.scene, { entity }).playBreakParticles(
        component.count >= 4 ? 'band' : 'wood',
      );
      component.count -= 1;
      this.scene?.events.publish({ id: 'poke' });
    }
  }

  private async explodeBarrel(entity: BarrelBlockEntity) {
    // Find out surrounding barrels for the chain reaction
    const otherBarrels: BarrelBlockEntity[] = [];
    blockNearestNeighbors(
      this.scene.sessionEntity.c.map,
      entity,
      (neighbor) => {
        if (neighbor === entity) return;
        if (!neighbor.c.blockBarrel) return;
        const barrel = neighbor as BarrelBlockEntity;
        if (barrel.c.blockBarrel.count === 0) return;
        if (otherBarrels.includes(barrel)) return;
        otherBarrels.push(barrel);
      },
    );

    // Explode the barrel
    entity.c.blockBarrel.count = 0;
    const effect = new BarrelBlockEffect(this.scene, { entity });
    app.sound.play('puzzle-door-destroy');
    effect.playExplodeAnimation();
    effect.playBreakParticles('all');
    despawnBlockEntity(this.scene, entity);
    await waitForItPromise(200);

    // Trigger the chain reaction
    const promises = otherBarrels.map((barrel) => this.explodeBarrel(barrel));
    await Promise.all(promises);
  }
}
